import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { EmailChoiceDialog } from './EmailChoiceDialog';
import { useUser } from '../hooks/useUser';
import { useTestMode } from '../hooks/useTestMode';

interface PreTestFormProps {
  onComplete: (userId: string, firstName: string) => void;
}

export function PreTestForm({ onComplete }: PreTestFormProps) {
  const { isAuthenticated, userProfile, triggerProfileSync } = useUser();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showChoiceDialog, setShowChoiceDialog] = useState(false);
  const [, setExistingProfiles] = useState<any[]>([]);

  // Guard to prevent duplicate auto-continue calls
  const hasAutoCompletedRef = React.useRef(false);

  const { isTestMode } = useTestMode();

  useEffect(() => {
    if (!isTestMode) return;
    setFirstName('Test');
    setEmail('test+' + Date.now() + '@kariton.com');
  }, [isTestMode]);

  // Auto-continue for authenticated users (ONE TIME ONLY)
  useEffect(() => {
    if (isAuthenticated && userProfile && !hasAutoCompletedRef.current) {
      hasAutoCompletedRef.current = true;
      console.log('✅ PreTestForm: Auto-continuing with authenticated profile (ONE TIME)');
      console.log('📋 PreTestForm: userId:', userProfile.id, 'firstName:', userProfile.first_name);
      onComplete(userProfile.id, userProfile.first_name || 'User');
    }
  }, [isAuthenticated, userProfile, onComplete]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // CRITICAL GUARD: Authenticated users should NEVER reach this code
    if (isAuthenticated) {
      console.error('❌ PreTestForm: handleSubmit called for authenticated user - this is a bug!');
      console.error('❌ PreTestForm: userProfile:', userProfile);
      setError('You are already logged in. Redirecting to test...');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!firstName.trim()) {
      setError('Please enter your first name');
      return;
    }

    setLoading(true);

    try {
      // Check if email already exists
      const { data: profiles, error: checkError } = await supabase
        .from('user_profiles')
        .select('id, first_name, auth_user_id, created_at')
        .eq('email', email.toLowerCase());

      if (checkError) {
        console.error('Error checking existing profiles:', checkError);
        // Continue with anonymous creation if check fails
        await createAnonymousProfile();
        return;
      }

      if (profiles && profiles.length > 0) {
        // Email exists, show choice dialog
        setExistingProfiles(profiles);
        setShowChoiceDialog(true);
        setLoading(false);
        return;
      }

      // No existing profiles, create anonymous profile
      await createAnonymousProfile();
    } catch (err) {
      console.error('Error in form submission:', err);
      setError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
      setLoading(false);
    }
  };

  const createAnonymousProfile = async () => {
    // CRITICAL GUARD: Prevent authenticated users from creating anonymous profiles
    if (isAuthenticated) {
      console.error('❌ PreTestForm: createAnonymousProfile called for authenticated user - BLOCKING');
      console.error('❌ PreTestForm: This should NEVER happen. userProfile:', userProfile);
      setError('You are already logged in. This operation is not allowed.');
      setLoading(false);
      return;
    }

    try {
      // Use RPC to create the profile and get { id, access_token } atomically
      const { data, error } = await supabase.rpc('create_anonymous_profile', {
        p_email: email.toLowerCase(),
        p_first_name: firstName.trim(),
      });

      if (error) {
        console.error('Insert error:', error);
        throw new Error('Failed to create profile. Please try again.');
      }

      // RPC may return a single row or an array with one row depending on PostgREST
      const row = Array.isArray(data) ? data[0] : data;

      if (row && row.id && row.access_token) {
        // Store access token for future use
        localStorage.setItem('user_access_token', row.access_token);
        console.log('✅ PreTestForm: Profile created, triggering sync before navigation...');

        // CRITICAL: Sync the profile with UserContext before navigating
        // This ensures userId in state matches the access_token
        await triggerProfileSync();
        console.log('✅ PreTestForm: Sync complete, ready to navigate');

        onComplete(row.id as string, firstName.trim());
      } else {
        console.error('RPC returned unexpected payload:', data);
        throw new Error('Failed to create profile. Please try again.');
      }
    } catch (err) {
      console.error('Error creating anonymous profile:', err);
      setError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnonymousChoice = async () => {
    setShowChoiceDialog(false);
    setLoading(true);
    await createAnonymousProfile();
  };

  const handleMagicLinkChoice = async () => {
    setShowChoiceDialog(false);
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/send-magic-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.toLowerCase() }),
      });

      const data = await response.json();

      if (data.success) {
        setError(''); // Clear any previous errors
        // Show success message
        setError(`✅ Magic link sent to ${email}! Check your email and click the link to access your account.`);
        
        // If in development, also show the debug URL
        if (data.debugUrl) {
          console.log('Development magic link:', data.debugUrl);
          // You could also show this to the user in development
          setError(`✅ Magic link sent! In development mode, use this link: ${data.debugUrl}`);
        }
      } else {
        setError(data.error || 'Failed to send magic link. Please try again.');
      }
    } catch (err) {
      console.error('Error sending magic link:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setShowChoiceDialog(false);
    setLoading(false);
  };

  // Authenticated users are auto-redirected via useEffect
  // Show minimal loading state during redirect
  if (isAuthenticated && userProfile) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-md mx-auto"
      >
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl border border-white/10 p-8">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-white mb-2">
              Welcome back, {userProfile.first_name || 'User'}!
            </h2>
            <p className="text-gray-300 mb-6">
              Starting your test...
            </p>
            <div className="flex justify-center">
              <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-md mx-auto"
    >
      <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl border border-white/10 p-8">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Before we begin...
        </h2>
        <p className="text-gray-300 mb-8">
          Please provide your information to start the personality test
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
              placeholder="Enter your first name"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
              placeholder="your@email.com"
              required
              disabled={loading}
            />
          </div>

          {error && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-red-400 text-sm"
            >
              {error}
            </motion.p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Processing...' : 'Continue to Test'}
          </button>
        </form>
      </div>

      {/* Email Choice Dialog - ONLY for anonymous users */}
      {showChoiceDialog && !isAuthenticated && (
        <EmailChoiceDialog
          email={email}
          onAnonymous={handleAnonymousChoice}
          onMagicLink={handleMagicLinkChoice}
          onClose={handleCloseDialog}
        />
      )}
    </motion.div>
  );
}